import { Alert, Form, Input, Modal } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props<V> = {
    open: boolean;
    title: string;
    /** Destructive or money-moving action: red OK button and a warning banner. */
    danger?: boolean;
    warning?: string;
    okText?: string;
    /** Extra form items rendered above the reason (names land in the submitted values). */
    children?: ReactNode;
    initialValues?: Partial<V>;
    onCancel: () => void;
    /** Throwing keeps the modal open (the caller already notified the error). */
    onConfirm: (values: V & { reason: string }) => Promise<void>;
};

/** Confirm dialog of every console write: a typed reason (3..500 chars) is mandatory and goes to the audit trail. */
export function SaSmsReasonModal<V extends object = object>({
    open,
    title,
    danger,
    warning,
    okText,
    children,
    initialValues,
    onCancel,
    onConfirm,
}: Props<V>) {
    const { t } = useTranslation(['sa-sms']);
    const [form] = Form.useForm<V & { reason: string }>();
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) form.resetFields();
    }, [open, form]);

    const submit = async () => {
        const values = await form.validateFields();
        setBusy(true);
        try {
            await onConfirm({ ...values, reason: values.reason.trim() });
        } catch {
            return; // error already shown by the write hook; stay open for a retry
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            title={title}
            okText={okText ?? t('actions.confirm')}
            cancelText={t('actions.cancel')}
            okButtonProps={{ danger, loading: busy }}
            onOk={submit}
            onCancel={onCancel}
            maskClosable={false}
            destroyOnClose
        >
            {warning && (
                <Alert
                    type={danger ? 'warning' : 'info'}
                    showIcon
                    message={warning}
                    style={{ marginBottom: 16 }}
                />
            )}
            <Form form={form} layout="vertical" initialValues={initialValues as never}>
                {children}
                <Form.Item
                    name="reason"
                    label={t('reason.label')}
                    rules={[
                        { required: true, whitespace: true, message: t('reason.required') },
                        { min: 3, message: t('reason.tooShort') },
                        { max: 500, message: t('reason.tooLong') },
                    ]}
                >
                    <Input.TextArea
                        rows={3}
                        maxLength={500}
                        showCount
                        placeholder={t('reason.placeholder')}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}
